import {Group, GroupItem, GroupSection} from "@/pb/api/devplan/types/grouping";
import {LucideIcon} from "@/pb/api/devplan/types/common";
import {ProjectStatus, ProjectWithDocs} from "@/pb/api/devplan/types/documents";
import {doneSectionKey, backlogSectionKey, laterSectionKey, nextSectionKey, nowSectionKey} from "@/app/data/common";
import logger from "@/app/logger";

export const sectionsOrder = [nowSectionKey, nextSectionKey, backlogSectionKey, doneSectionKey]

export function isDefaultSection(sectionKey: string): boolean {
  return defaultSections.hasOwnProperty(sectionKey)
}

/**
 * Determines if a section can be edited or deleted.
 * Returns true for custom sections and editable default sections (now, next, later).
 * Returns false for protected default sections (done, backlog).
 */
export function isEditableSection(sectionKey: string): boolean {
  // All custom sections are editable
  if (!isDefaultSection(sectionKey)) {
    return true
  }
  // Default sections: now, next, later are editable
  // done and backlog are not editable
  return sectionKey !== doneSectionKey && sectionKey !== backlogSectionKey
}

/**
 * Calculate the new index for a section being reordered.
 * @param overItemKey - The key of the item being dropped on
 * @param overEntryType - The type of entry being dropped on ("section" | other)
 * @param overEntryParentKey - The parent key of the entry being dropped on
 * @param topLevelItems - Array of top-level item keys in the group
 * @param itemByKey - Map of all items by key for lookup
 * @returns The new index where the section should be placed, or -1 if position cannot be determined
 */
export function calculateSectionNewIndex(
  overItemKey: string,
  overEntryType: string | undefined,
  overEntryParentKey: string | undefined,
  topLevelItems: string[],
  itemByKey: Record<string, {type?: {$case: string}, childItems: string[], key: string}>
): number {
  // Case 1: Dropping on another section - place at that section's position
  if (overEntryType === "section") {
    return topLevelItems.indexOf(overItemKey)
  }

  // Case 2: Dropping on a child item - find the parent section's position
  if (overEntryParentKey) {
    return topLevelItems.indexOf(overEntryParentKey)
  }

  // Case 3: Dropping on a root-level item
  const itemIndex = topLevelItems.indexOf(overItemKey)
  if (itemIndex !== -1) {
    return itemIndex
  }

  // Case 4: Item might be a project - find which section contains it
  const item = itemByKey[overItemKey]
  if (item) {
    const parentSection = Object.values(itemByKey).find(
      i => i.type?.$case === "section" && i.childItems.includes(overItemKey)
    )
    if (parentSection) {
      return topLevelItems.indexOf(parentSection.key)
    }
  }

  return -1
}

export const defaultSections: Record<string, GroupSection> = {
  [doneSectionKey]: {
    name: "Done",
    icon: LucideIcon.create({name: "CheckCircle", color: "#cccccc"}),
  },
  [backlogSectionKey]: {
    name: "Backlog",
    icon: LucideIcon.create({name: "BookDashed", color: "#5eceef"}),
  },
  [laterSectionKey]: {
    name: "Later",
    icon: LucideIcon.create({name: "Calendar", color: "#a855f7"}),
  },
  [nowSectionKey]: {
    name: "Now",
    icon: LucideIcon.create({name: "Clock", color: "#22c55e"}),
  },
  [nextSectionKey]: {
    name: "Next",
    icon: LucideIcon.create({name: "ArrowRight", color: "#3b82f6"}),
  },
}

export function getTopLevelItems(): GroupItem[] {
  return sectionsOrder.map(key => {
    const section = defaultSections[key];
    return GroupItem.create({
      key,
      entityId: key,
      rootOnly: true,
      type: {$case: "section", value: {...section}},
      childItems: [],
      noDrag: false,
      noDrop: key === doneSectionKey,
      childrenNoDrag: key === doneSectionKey,
    });
  });
}

/**
 * Converts a hex color string to an RGB color string with the specified opacity.
 * @param hex - Hex color string (e.g., "#FF5733" or "FF5733")
 * @param opacity - Opacity value between 0 and 1 (default: 0.05)
 * @returns RGB color string (e.g., "rgb(255, 87, 51, 0.05)") or undefined if hex is invalid
 */
export function hexToRgba(hex: string | undefined, opacity: number = 0.05): string | undefined {
  if (!hex) {
    return undefined
  }
  const cleaned = hex.replace('#', '');
  const r = parseInt(cleaned.slice(0, 2), 16);
  const g = parseInt(cleaned.slice(2, 4), 16);
  const b = parseInt(cleaned.slice(4, 6), 16);
  return `rgb(${r}, ${g}, ${b}, ${opacity})`;
}

export function areStringArraysEqualOrdered(arr1: string[], arr2: string[]): boolean {
  if (arr1.length !== arr2.length) {
    return false;
  }
  for (let i = 0; i < arr1.length; i++) {
    if (arr1[i] !== arr2[i]) {
      return false;
    }
  }
  return true;
}

/**
 * Migrates old "scratch-pad-projects" section key to "backlog-projects".
 * If a backlog section already exists, merges projects from the old section into it.
 * Otherwise, renames the old section to use the new key.
 * @param g - The group to migrate
 * @returns true if migration was performed, false otherwise
 */
export function migrateScratchPadToBacklog(g: Group): boolean {
  const oldScratchPadKey = "scratch-pad-projects"
  const oldScratchPadSection = g.items.find(i => i.key === oldScratchPadKey && i.type?.$case === "section")
  const existingBacklogSection = g.items.find(i => i.key === backlogSectionKey && i.type?.$case === "section")
  if (!oldScratchPadSection) {
    return false
  }
  logger.debug({groupId: g.id}, "Migrating scratch-pad-projects to backlog-projects")
  const oldSectionProjects = oldScratchPadSection.childItems || []
  if (existingBacklogSection) {
    // If backlog section already exists, merge projects from old section into it
    const existingProjects = new Set(existingBacklogSection.childItems)
    const mergedProjects = [...existingBacklogSection.childItems, ...oldSectionProjects.filter(p => !existingProjects.has(p))]
    g.items = g.items.map(item => {
      if (item.key === backlogSectionKey) {
        return {...item, childItems: mergedProjects}
      }
      // Remove the old section
      if (item.key === oldScratchPadKey) {
        return null
      }
      return item
    }).filter((item): item is GroupItem => item !== null)
    // Remove old key from topLevelItems
    g.topLevelItems = g.topLevelItems.filter(key => key !== oldScratchPadKey)
  } else {
    // No existing backlog section, just rename the old one
    g.items = g.items.map(item => {
      if (item.key === oldScratchPadKey) {
        return {...item, key: backlogSectionKey}
      }
      return item
    })
    // Update the key in topLevelItems
    g.topLevelItems = g.topLevelItems.map(key => key === oldScratchPadKey ? backlogSectionKey : key)
  }
  return true
}

/**
 * Place an existing or new project into a target section's childItems.
 * Removes the project id from every other section's childItems first (so a default
 * Backlog placement from groupInitializer is overwritten cleanly) and appends it
 * to the target section. Adds the project GroupItem if missing.
 * Returns the updated group; returns the original group if the target section
 * does not exist or is not actually a section.
 */
export function placeProjectIntoSection(g: Group, projectId: string, targetSectionKey: string): Group {
  const target = g.items.find(i => i.key === targetSectionKey && i.type?.$case === "section")
  if (!target) {
    return g
  }
  const hasProjectItem = g.items.some(i => i.key === projectId && i.type?.$case === "project")
  const items = (hasProjectItem
    ? g.items
    : [...g.items, GroupItem.create({key: projectId, entityId: projectId, type: {$case: "project", value: {}}})]
  ).map(item => {
    if (item.type?.$case !== "section") {
      return item
    }
    if (item.key === targetSectionKey) {
      const withoutDup = item.childItems.filter(id => id !== projectId)
      return {...item, childItems: [...withoutDup, projectId]}
    }
    if (item.childItems.includes(projectId)) {
      return {...item, childItems: item.childItems.filter(id => id !== projectId)}
    }
    return item
  })
  return {...g, items}
}

export function groupInitializer(g: Group, projects: ProjectWithDocs[]): Group | undefined {
  // Client snapshots can be partial while the Roadmap loads, so only reconcile projects present here.
  const validProjects = projects.filter(p => Boolean(p.project?.id))
  if (validProjects.length === 0) {
    return undefined
  }
  // Migration: Handle old "scratch-pad-projects" key and migrate to "backlog-projects"
  const needsMigration = migrateScratchPadToBacklog(g)
  const existingProjectIds = new Set(g.items.filter(i => i.type?.$case === "project").map(i => i.entityId))
  const allProjectIds = new Set(validProjects.map(p => p.project!.id))
  const doneSection = g.items.find(i => i.key === doneSectionKey)
  const allChildren = new Set(g.items.flatMap(i => i.childItems))
  const addToBacklog: string[] = [];
  const addToDone: string[] = [];
  const itemsToAdd: GroupItem[] = [];
  const doneProjects = validProjects.filter(p => p.project?.details?.status === ProjectStatus.DONE)
  const doneIds = new Set(doneProjects.map(p => p.project!.id))
  const existingDoneProjectIds = new Set(doneSection?.childItems?.filter(k => doneIds.has(k)) || [])
  // Do not reopen Done projects omitted from a partial snapshot.
  const removeFromDone = doneSection?.childItems.filter(id => allProjectIds.has(id) && !doneIds.has(id)) ?? []
  const removeFromDoneIds = new Set(removeFromDone)
  const topLevelItems = g.topLevelItems
  const expectedTopLevelItems = getTopLevelItems()
  const expectedTopLevelKeys = expectedTopLevelItems.map(i => i.key)
  
  // Identify custom sections (sections that are not default sections)
  const defaultSectionKeys = new Set(expectedTopLevelKeys)
  const existingCustomSections = g.items.filter(
    i => i.type?.$case === "section" && !defaultSectionKeys.has(i.key)
  )
  const existingCustomSectionKeys = existingCustomSections.map(s => s.key)
  
  // Preserve the exact order of topLevelItems - don't reorder anything
  // This allows custom sections to be placed anywhere, including between default sections
  // Only add missing default sections and new custom sections
  const mergedTopLevelKeys = [...topLevelItems] // Start with existing order
  const seenKeys = new Set(topLevelItems)
  
  // Required default sections (done, backlog) - always add if missing
  // Optional editable default sections (now, next, later) - only add if they exist in items
  // (meaning they weren't deleted, just missing from topLevelItems)
  const requiredDefaultKeys = new Set([doneSectionKey, backlogSectionKey])
  const optionalDefaultKeys = new Set([nowSectionKey, nextSectionKey, laterSectionKey])
  expectedTopLevelKeys.forEach(defaultKey => {
    if (!seenKeys.has(defaultKey)) {
      const isRequired = requiredDefaultKeys.has(defaultKey)
      const isOptional = optionalDefaultKeys.has(defaultKey)
      const existsInItems = g.items.some(i => i.key === defaultKey)
      // Add required sections if missing, or optional sections if they exist in items
      if (isRequired || (isOptional && existsInItems)) {
        mergedTopLevelKeys.push(defaultKey)
        seenKeys.add(defaultKey)
      }
    }
  })
  
  // Add any new custom sections that aren't in topLevelItems yet (at the end)
  existingCustomSectionKeys.forEach(customKey => {
    if (!seenKeys.has(customKey)) {
      mergedTopLevelKeys.push(customKey)
      seenKeys.add(customKey)
    }
  })
  
  const sameTopLevel = areStringArraysEqualOrdered(topLevelItems, mergedTopLevelKeys)
  if (!sameTopLevel) {
    expectedTopLevelItems.forEach(i => {
      const isRequired = requiredDefaultKeys.has(i.key)
      const existsInItems = g.items.some(item => item.key === i.key)
      // Only add to itemsToAdd if the section needs to be created (doesn't exist in items)
      // Required sections: always create if missing
      // Optional sections: never recreate if deleted (only add if they exist in items, but if they exist, don't add to itemsToAdd)
      if (!topLevelItems.includes(i.key) && !existsInItems) {
        if (isRequired) {
          // Required section missing - create it
          itemsToAdd.push(i)
        }
        // Optional sections that don't exist in items were deleted - don't recreate them
      }
    })
    // Ensure custom sections are in items array
    existingCustomSections.forEach(customSection => {
      if (!g.items.find(i => i.key === customSection.key)) {
        itemsToAdd.push(customSection)
      }
    })
  }

  validProjects.forEach(p => {
    const projectId = p.project!.id;
    const done = doneIds.has(projectId)
    if (!existingProjectIds.has(projectId)) {
      itemsToAdd.push(GroupItem.create({
        key: projectId,
        entityId: projectId,
        type: {$case: "project", value: {}},
      }))
    }
    if (done) {
      if (!existingDoneProjectIds.has(projectId)) {
        addToDone.push(projectId)
      }
      return
    }
    // Add to backlog if it is not done, not a child item of another entry
    if (!existingDoneProjectIds.has(projectId) && (!allChildren.has(projectId) || removeFromDoneIds.has(projectId))) {
      addToBacklog.push(projectId)
    }
  })
  const needsDefaultSectionFlagUpdate = g.items.some(item =>
    defaultSections[item.key] &&
    item.type?.$case === "section" &&
    (item.noDrag !== false ||
      item.noDrop !== (item.key === doneSectionKey) ||
      item.childrenNoDrag !== (item.key === doneSectionKey))
  )

  if (sameTopLevel && addToBacklog.length === 0 && addToDone.length === 0
    && itemsToAdd.length === 0 && removeFromDone.length === 0 && !needsDefaultSectionFlagUpdate && !needsMigration) {
    logger.debug({groupId: g.id}, "Group update not needed");
    return undefined
  }
  logger.debug({groupId: g.id, allProjectIds, existingProjectIds}, "Updating group");
  const allItems: GroupItem[] = [...g.items, ...itemsToAdd]
  return {
    ...g,
    topLevelItems: mergedTopLevelKeys,
    items: allItems.map(gi => {
      const item = {...gi}
      if (item.key === backlogSectionKey) {
        item.childItems = [...addToBacklog, ...item.childItems]
      }
      if (item.key === doneSectionKey) {
        item.childItems = [...addToDone, ...item.childItems.filter(i => !removeFromDoneIds.has(i))]
      } else {
        item.childItems = item.childItems.filter(i => !doneIds.has(i))
      }

      // Normalize default section flags for existing groups
      if (defaultSections[item.key] && item.type?.$case === "section") {
        item.noDrag = false
        item.noDrop = item.key === doneSectionKey
        item.childrenNoDrag = item.key === doneSectionKey
      }
      return item
    }),
  }
}
